import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api, unwrapArray } from "@/lib/api";
import { LeadMergeDialog } from "./LeadMergeDialog";

interface Dup {
  id: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  email?: string;
  phone?: string;
  lead_status: string;
  owner?: string | null;
  reasons: string[];
  strong: boolean;
}

/** Leads that look like this one (same email or phone, or only the same company name), with Compare and merge. Shows nothing when there are none. */
export function LeadDuplicates({ id, canMerge, onMerged }: { id: string; canMerge: boolean; onMerged: (keptId: string) => void }) {
  const [rows, setRows] = useState<Dup[]>([]);
  const [pair, setPair] = useState<[string, string] | null>(null);

  useEffect(() => {
    let live = true;
    api
      .get(`/api/Lead/Duplicates/${id}`)
      .then((r) => live && setRows(unwrapArray<Dup>(r)))
      .catch(() => live && setRows([]));
    return () => {
      live = false;
    };
  }, [id]);

  if (!rows.length) return null;
  return (
    <section className="flex flex-col gap-2 rounded-lg bg-warning-soft px-3.5 py-3">
      <h2 className="text-[11.5px] font-semibold uppercase tracking-wide text-warning">Possible duplicates · {rows.length}</h2>
      {rows.map((d) => (
        <div key={d.id} className="flex items-start justify-between gap-2 rounded-md bg-card/70 px-3 py-2">
          <div className="min-w-0">
            <Link to={`/leads/${d.id}`} className="text-[13px] font-semibold text-primary hover:underline">
              {[d.first_name, d.last_name].filter(Boolean).join(" ") || d.company_name}
            </Link>
            <div className="truncate text-xs text-muted-foreground">{[d.company_name, d.email, d.phone, d.owner ? `owned by ${d.owner}` : null].filter(Boolean).join(" · ")}</div>
            <div className="mt-0.5 flex flex-wrap gap-1">
              {d.reasons.map((r) => (
                <span key={r} className="rounded bg-warning/15 px-1.5 text-[10.5px] font-medium text-warning">
                  {r}
                </span>
              ))}
              {!d.strong && <span className="text-[10.5px] text-muted-foreground">Same company only: check before merging</span>}
            </div>
          </div>
          {canMerge && (
            <Button size="sm" variant="outline" onClick={() => setPair([id, d.id])}>
              Compare and merge
            </Button>
          )}
        </div>
      ))}
      <LeadMergeDialog open={!!pair} ids={pair} onClose={() => setPair(null)} onMerged={onMerged} />
    </section>
  );
}
